import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

function EvaluatorProgressFeed() {
  const { user } = useAuth();

  const [posts, setPosts] = useState([]);
  const [allowedFounderIds, setAllowedFounderIds] = useState([]);
  const [likesMap, setLikesMap] = useState({});

  /* LOAD SHARED FOUNDERS */
  useEffect(() => {
    if (!user?.uid) return;

    const loadSharedFounders = async () => {
      try {
        const q = query(
          collection(db, "shares"),
          where("evaluatorId", "==", user.uid)
        );
        const snap = await getDocs(q);
        setAllowedFounderIds(snap.docs.map((d) => d.data().founderId));
      } catch (err) {
        console.error("Failed to load shared founders", err);
      }
    };

    loadSharedFounders();
  }, [user?.uid]);

  /* LOAD FEED */
  useEffect(() => {
    if (!user?.uid || allowedFounderIds.length === 0) {
      setPosts([]);
      return;
    }

    const q = query(collection(db, "progressPosts"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      const filtered = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => allowedFounderIds.includes(p.founderId));
      setPosts(filtered);
    });

    return () => unsub();
  }, [allowedFounderIds, user?.uid]);

  /* REALTIME LIKES */
  useEffect(() => {
    if (!user?.uid || posts.length === 0) return;

    const unsubscribers = posts.map((p) => {
      const likeRef = doc(db, "progressPosts", p.id, "likes", user.uid);
      return onSnapshot(likeRef, (snap) => {
        setLikesMap((prev) => ({ ...prev, [p.id]: snap.exists() }));
      });
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [posts, user?.uid]);

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "";
    const date = timestamp.toDate();
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const toggleLike = async (postId, liked) => {
    try {
      const ref = doc(db, "progressPosts", postId, "likes", user.uid);
      const postRef = doc(db, "progressPosts", postId);

      if (liked) {
        await deleteDoc(ref);
        await updateDoc(postRef, { likesCount: increment(-1) });
      } else {
        await setDoc(ref, {
          evaluatorId: user.uid,
          likedAt: serverTimestamp(),
        });
        await updateDoc(postRef, {
          likesCount: increment(1),
          lastLikeAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error("Failed to toggle like", err);
    }
  };

  return (
    <div>
      <h1>Progress Feed</h1>
      <p style={{ marginTop: 8, color: "#64748b" }}>
        Track daily progress updates from startups you are evaluating.
      </p>

      <div style={{ marginTop: 28, maxWidth: 760 }}>
        {posts.length === 0 ? (
          <div className="card" style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>
            No updates from shared startups yet.
          </div>
        ) : (
          posts.map((p) => (
            <div
              key={p.id}
              className="card"
              style={{
                padding: 22,
                marginBottom: 22,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-4px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <strong style={{ fontSize: 15 }}>{p.startupName}</strong>

              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                {p.founderName} · {formatDate(p.createdAt)}
              </div>

              <p style={{ marginTop: 10, lineHeight: 1.6 }}>{p.text}</p>

              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt="Progress update"
                  style={{
                    marginTop: 12,
                    width: "100%",
                    borderRadius: 14,
                    maxHeight: 420,
                    objectFit: "cover",
                  }}
                />
              )}

              {/* LIKE BUTTON */}
              <button
                onClick={() => toggleLike(p.id, likesMap[p.id])}
                style={{
                  marginTop: 14,
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: "1px solid #e2e8f0",
                  background: likesMap[p.id] ? "#fee2e2" : "#f8fafc",
                  color: likesMap[p.id] ? "#dc2626" : "#475569",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                {likesMap[p.id] ? "❤️ Liked" : "🤍 Like"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EvaluatorProgressFeed;
